import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Dashboard.css';
import DashboardCard from '../components/DashboardCard';
import SetupStatusWidget from '../components/SetupStatusWidget';
import GraphConnectionCard from '../components/setup/GraphConnectionCard';
import GoogleConnectionCard from '../components/setup/GoogleConnectionCard';
import AddinDeploymentCard from '../components/setup/AddinDeploymentCard';
import GmailAddonDeploymentCard from '../components/setup/GmailAddonDeploymentCard';
import SetupWizardModal from '../components/setup/SetupWizardModal';
import { getInitials } from '../utils/initials';
import { ReactComponent as AlertIcon } from '../icons/alert.svg';
import { ReactComponent as UserIcon } from '../icons/user.svg';
import { ReactComponent as GroupIcon } from '../icons/groups.svg';
import { ReactComponent as SignatureIcon } from '../icons/signatures.svg';
import DashboardUserTable from '../components/DashboardUserTable';
import DashboardGroupTable from '../components/DashboardGroupTable';
import DashboardSignatureTable from '../components/DashboardSignatureTable';
import SystemNotifications from '../components/SystemNotifications';
import { api, endpoints } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { useT } from '../i18n';

const messages = {
  docTitle: { tr: 'Anasayfa - Signature Manager', en: 'Dashboard - Signature Manager' },
  otherProvider: {
    tr: 'Zaten bir dizin sağlayıcısına bağlısınız. Değiştirmek için önce mevcut bağlantıyı kaldırın.',
    en: 'You’re already connected to a directory provider. Remove the current connection first to switch.',
  },
  statsError: { tr: 'Dashboard verileri alınamadı!', en: 'Couldn’t load dashboard data!' },
  welcome: { tr: 'Tekrar hoşgeldin, {name}', en: 'Welcome back, {name}' },
  user: { tr: 'Kullanıcı', en: 'there' },
  title: { tr: 'İmza Kontrol Paneli', en: 'Signature Control Panel' },
  profile: { tr: 'Profil ve ayarlar', en: 'Profile and settings' },
  totalStaff: { tr: 'Toplam Personel', en: 'Total People' },
  groups: { tr: 'Gruplar', en: 'Groups' },
  templates: { tr: 'İmza Şablonları', en: 'Signature Templates' },
  alerts: { tr: 'Uyarılar', en: 'Alerts' },
};

function Dashboard({ showToast }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useT(messages);

  useEffect(() => {
    document.title = t('docTitle');
  }, [t]);

  // Backend redirects here after the Microsoft admin-consent callback with
  // ?ms_connected=1|0&reason=... — read it once, then strip it from the URL
  // so refreshing/sharing the link doesn't replay a stale success/error.
  const [msConnectedResult, setMsConnectedResult] = useState(null);
  const [googleConnectedResult, setGoogleConnectedResult] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let changed = false;
    if (params.has('ms_connected')) {
      setMsConnectedResult({ connected: params.get('ms_connected'), reason: params.get('reason') });
      changed = true;
    }
    if (params.has('google_connected')) {
      setGoogleConnectedResult({ connected: params.get('google_connected'), reason: params.get('reason') });
      changed = true;
    }
    if (changed) navigate('/dashboard', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isOrgAdmin = user?.role === 'org_admin' || user?.role === 'super_admin';

  // Shared by SetupWizardModal and the persistent connection/Add-in cards
  // below — one fetch, so completing a step in the modal immediately hides
  // the cards too instead of waiting for a reload.
  const [msStatus, setMsStatus] = useState(null);
  const fetchMsStatus = React.useCallback(async () => {
    try {
      const res = await api.get(endpoints.organizations.graphStatus);
      setMsStatus(res);
    } catch {
      setMsStatus(null);
    }
  }, []);
  const [googleStatus, setGoogleStatus] = useState(null);
  const fetchGoogleStatus = React.useCallback(async () => {
    try {
      const res = await api.get(endpoints.organizations.googleStatus);
      setGoogleStatus(res);
    } catch {
      setGoogleStatus(null);
    }
  }, []);
  useEffect(() => {
    if (isOrgAdmin) {
      fetchMsStatus();
      fetchGoogleStatus();
    }
  }, [isOrgAdmin, fetchMsStatus, fetchGoogleStatus]);
  const msSetupComplete = msStatus?.connected && msStatus?.addin_deployed;
  const googleSetupComplete = googleStatus?.connected && googleStatus?.addon_deployed;
  // An org connects to exactly one directory provider at a time — once one
  // is connected, the other provider's card shows a disabled hint instead
  // of an actionable "Connect" button (disconnect first to switch).
  const otherProviderHint = t('otherProvider');

  const [stats, setStats] = useState({
    totalUsers: 0,
    departmentCount: 0,
    signatureTemplates: 0
  });
  const [alertCount, setAlertCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [setupStatus, setSetupStatus] = useState(null);

  useEffect(() => {
    // Only fetch if user is authenticated
    if (!user) return;
    
    const fetchSetupStatus = async () => {
      try {
        const data = await api.get('/api/exchange/my-setup-status/');
        setSetupStatus(data);
      } catch (err) {
        console.error('Setup status error:', err);
        // Don't show error - setup status is optional
        setSetupStatus(null);
      }
    };
    
    const fetchStats = async () => {
      setLoading(true);
      try {
        const data = await api.get(endpoints.signatures.dashboard.stats);
        // Only the template count comes from here. Personnel and groups come
        // from the directory (fetchUsers below): this endpoint's total_users
        // counts panel login accounts and total_groups only groups with a
        // template, and since both requests run at once, whichever finished
        // last used to win — the tiles sometimes showed 3 people / 1 group
        // for a 273-person directory (Atako, 2026-10-29).
        setStats(prev => ({ ...prev, signatureTemplates: data.total_templates || 0 }));
      } catch (err) {
        console.error('Dashboard stats error:', err);
        showToast && showToast(t('statsError'));
      } finally {
        setLoading(false);
      }
    };
    
    const fetchAlerts = async () => {
      try {
        // Fetch both signature alerts and system notifications
        const [signatureAlerts, systemNotifications] = await Promise.all([
          api.get(endpoints.signatures.alerts).catch(() => []),
          api.get(endpoints.admin.notifications.user).catch(() => ({ unread_count: 0 }))
        ]);
        
        const signatureAlertCount = Array.isArray(signatureAlerts) ? signatureAlerts.length : 0;
        const systemNotificationCount = systemNotifications.unread_count || 0;
        
        setAlertCount(signatureAlertCount + systemNotificationCount);
      } catch (err) {
        console.error('Alerts error:', err);
        setAlertCount(0);
      }
    };
    
    const fetchUsers = async () => {
      try {
        const data = await api.get(endpoints.users);
        setUsers(data);
        setStats(prev => ({
          ...prev,
          totalUsers: data.length,
          // Bir kullanıcı birden fazla gruba dahil olabilir — grup sayısı
          // sadece "birincil" gruba değil, tüm üyeliklere göre hesaplanır
          // (Gruplar sayfasındaki gerçek grup sayısıyla tutarlı olması için).
          departmentCount: new Set(data.flatMap(u => (u.groups && u.groups.length > 0 ? u.groups : [u.group]).filter(Boolean))).size
        }));
      } catch (err) {
        console.error('Users error:', err);
        setUsers([]);
        setStats(prev => ({ ...prev, totalUsers: 0, departmentCount: 0 }));
      }
    };
    
    fetchSetupStatus();
    fetchStats();
    fetchAlerts();
    fetchUsers();
    
    const interval = setInterval(fetchAlerts, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, showToast]);

  if (loading) return <div style={{padding: 32}}>{t('loading')}</div>;

  return (
    <div className="dashboard-container">
      <div className='dashboard-head'>
        <div className='dashboard-entry'>
          <p>{t('welcome', { name: user?.first_name || t('user') })} <span>👋</span></p>
          <h1>{t('title')}</h1>
        </div>
        <div className='dashboard-account'>
          <button className='alert-icon' type="button" onClick={() => navigate('/alerts')}>
            <AlertIcon className="alert-icon" />
            {alertCount > 0 && <span className="dashboard-alert-badge">{alertCount}</span>}
          </button>
          <button
            className="dashboard-avatar"
            type="button"
            onClick={() => navigate('/settings')}
            aria-label={t('profile')}
            title={t('profile')}
          >
            {getInitials(user)}
          </button>
        </div>
      </div>
      
      {/* System Notifications - shown only if there are notifications */}
      <SystemNotifications />

      {isOrgAdmin && (
        <SetupWizardModal
          organizationId={user?.organization}
          msResultParam={msConnectedResult}
          googleResultParam={googleConnectedResult}
          msStatus={msStatus}
          googleStatus={googleStatus}
          onStatusChange={() => { fetchMsStatus(); fetchGoogleStatus(); }}
        />
      )}

      {isOrgAdmin && !msSetupComplete && (
        <GraphConnectionCard
          resultParam={msConnectedResult}
          disabledReason={googleStatus?.connected ? otherProviderHint : null}
        />
      )}
      {isOrgAdmin && !msSetupComplete && msStatus?.connected && (
        <AddinDeploymentCard onDeployed={fetchMsStatus} />
      )}
      {/* Launch is Microsoft 365 only — Google stays manageable for an
          organization that already connected it, hidden for everyone else. */}
      {isOrgAdmin && !googleSetupComplete && googleStatus?.connected && (
        <GoogleConnectionCard resultParam={googleConnectedResult} />
      )}
      {isOrgAdmin && !googleSetupComplete && googleStatus?.connected && (
        <GmailAddonDeploymentCard />
      )}

      {/* Setup Status Widget - shown only if setup is not complete */}
      {setupStatus && !setupStatus.is_setup_complete && (
        <SetupStatusWidget status={setupStatus} />
      )}
      
      <div className="card-wrapper">
        <DashboardCard CardIcon={<UserIcon />} title={t('totalStaff')} count={stats.totalUsers} to="/users" />
        <DashboardCard CardIcon={<GroupIcon />} title={t('groups')} count={stats.departmentCount} to="/groups" />
        <DashboardCard CardIcon={<SignatureIcon />} title={t('templates')} count={stats.signatureTemplates} to="/signatures" />
        <DashboardCard CardIcon={<AlertIcon />} title={t('alerts')} count={alertCount} variant="dark" to="/alerts" />
      </div>
      <DashboardUserTable users={users} />
      <DashboardGroupTable />
      <DashboardSignatureTable />
    </div>
  );
}

export default Dashboard;
